<template>
  <div v-if="isDevelopment" class="fixed bottom-4 right-4 z-50">
    <Button 
      @click="showDebugPanel = !showDebugPanel"
      icon="pi pi-flag"
      severity="info"
      rounded
      text
      v-tooltip="'Feature Flags Debug'"
    />
    
    <Dialog 
      v-model:visible="showDebugPanel" 
      header="Feature Flags Debug" 
      :style="{ width: '500px' }"
      position="bottomright"
      :modal="false"
      :draggable="false"
      :resizable="false"
    >
      <div class="space-y-4">
        <div class="flex items-center justify-between">
          <h6 class="text-sm font-semibold text-surface-700">Available Feature Flags</h6>
          <Button 
            @click="refreshFeatureFlags"
            icon="pi pi-refresh"
            size="small"
            outlined
            :loading="refreshing"
            label="Refresh"
          />
        </div>
        
        <div v-if="Object.keys(allFlags).length === 0" class="text-center py-4 text-surface-500">
          No feature flags found
        </div>
        
        <div v-else class="space-y-2">
          <div 
            v-for="(flag, name) in allFlags" 
            :key="name"
            class="flex items-center justify-between p-3 border border-surface-200 rounded-lg"
          >
            <div class="flex-1">
              <div class="font-medium text-sm">{{ name }}</div>
              <div class="text-xs text-surface-500">
                Type: {{ flag.t }} | Value: {{ flag.v }}
              </div>
            </div>
            <Tag 
              :value="flag.v ? 'Enabled' : 'Disabled'"
              :severity="flag.v ? 'success' : 'secondary'"
              size="small"
            />
          </div>
        </div>
        
        <Divider />
        
        <div class="text-xs text-surface-500">
          <strong>Usage Examples:</strong><br/>
          • Template: <code>v-feature-flag="'integrations'"</code><br/>
          • Component: <code>&lt;FeatureFlag flag="integrations"&gt;</code><br/>
          • Script: <code>isFeatureEnabled('integrations')</code>
        </div>
      </div>
    </Dialog>
  </div>
</template>

<script setup lang="ts">
import { ref, computed } from 'vue';
import { useFeatureFlags } from '@/composables/useFeatureFlags';
import Button from 'primevue/button';
import Dialog from 'primevue/dialog';
import Tag from 'primevue/tag';
import Divider from 'primevue/divider';

const { getAllFeatureFlags, refreshFeatureFlags: refresh } = useFeatureFlags();

const showDebugPanel = ref(false);
const refreshing = ref(false);

const isDevelopment = computed(() => import.meta.env.DEV);
const allFlags = computed(() => getAllFeatureFlags.value);

const refreshFeatureFlags = async () => {
  refreshing.value = true;
  try {
    await refresh();
  } finally {
    refreshing.value = false;
  }
};
</script>
